import { isPathMatches } from '@terminal/components/app-header/utils/navigation-utils';
import {
  ChatInputComposer,
  ChatInputConfig,
  ChatSubmitEvent,
  SubmitOptions,
} from '@terminal/components/chat';
import { stripMentionMarkers } from '@terminal/components/chat/chat-input/mentions/markers';
import {
  POSTHOG_TRACKING_SOURCES,
  TrackingMetadata,
} from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useReasonerParams } from '@terminal/views/alfred/research/hooks/useReasonerParams';
import { postClonePublicConversation } from '@terminal/views/alfred/research/services/conversations-service';
import { useChat } from '@terminal/views/alfred/research/use-chat/useChat';
import { useChatModeStore } from '@terminal/views/alfred/use-chat-navigation/useChatNavigation';
import React, { useRef } from 'react';
import { useLocation } from 'react-router';

export interface ChatInputProps {
  config?: ChatInputConfig;
  submitCallback?: (message?: string) => void;
  trackingMetadata?: TrackingMetadata;
}

export const ChatDeepAnalysis = ({
  config,
  submitCallback,
  trackingMetadata,
}: ChatInputProps) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const multiSubmitQueueRef = useRef(Promise.resolve());
  const location = useLocation();
  const { remountChatInput } = (location.state ?? {}) as {
    remountChatInput?: number;
  };
  const {
    trackAlfredQuerySent,
    trackAlfredShare,
    trackAlfredConversationStarted,
  } = usePosthogEvents();
  const { conversationId, setReasonerParams } = useReasonerParams();

  const {
    activeConversation,
    postQuestion,
    abortRequest,
    clonePublicConversation,
    appendNewConversation,
    analysisMode,
  } = useChat(state => ({
    activeConversation: state.getActiveConversation(conversationId),
    postQuestion: state.postQuestion,
    abortRequest: state.abortRequest,
    clonePublicConversation: state.clonePublicConversation,
    appendNewConversation: state.appendNewConversation,
    analysisMode: state.analysisMode,
  }));

  const isAlfredPage = isPathMatches(['alfred']);
  const lastTask = activeConversation?.tasks?.at(-1);
  const lastState = lastTask?.states?.at(-1);
  const isLoading =
    isAlfredPage &&
    !!conversationId &&
    (!!lastState?.next || activeConversation?.status === 'Processing');

  const submitHandler = async (
    inputValue: string,
    onSubmitCallbackHandler: () => void,
    options?: SubmitOptions
  ) => {
    const question = inputValue.trim();
    const trackingValue = stripMentionMarkers(question);
    const isPromptRefined = options?.isPromptRefined;
    const shouldStartNewConversation = options?.startNewConversation;
    const mentions = options?.mentions;

    if (isLoading || !question) {
      return;
    }

    const submitQuestion = async () => {
      const captureQuerySent = (sentConversationId: string | undefined) => {
        if (trackingMetadata?.source) {
          trackAlfredQuerySent({
            source: trackingMetadata.source,
            type: analysisMode,
            via: trackingMetadata.via,
            value: trackingValue,
            conversationId: sentConversationId,
            isPromptRefined,
          });
        } else if (isAlfredPage) {
          trackAlfredQuerySent({
            source: POSTHOG_TRACKING_SOURCES.ALFRED,
            type: analysisMode,
            value: trackingValue,
            conversationId: sentConversationId,
            isPromptRefined,
          });
        }
      };

      if (
        !shouldStartNewConversation &&
        (activeConversation?.access_level === 'public' ||
          (!activeConversation && conversationId))
      ) {
        const response = await postClonePublicConversation(conversationId);

        if (!response.error) {
          const newConversationId = response.data.conversation_id;

          trackAlfredShare(POSTHOG_TRACKING_SOURCES.ALFRED, 'continue');

          if (!config?.alfredEverywhere) {
            setReasonerParams({
              conversationId: newConversationId,
            });
          }

          const conversation = await clonePublicConversation(newConversationId);

          if (conversation) {
            appendNewConversation(conversation);
          }

          await postQuestion({
            question,
            conversationId:
              isAlfredPage && newConversationId ? newConversationId : undefined,
            mentions,
          });

          captureQuerySent(newConversationId);

          return;
        }
      }

      const targetConversationId =
        shouldStartNewConversation || !isAlfredPage
          ? undefined
          : conversationId;
      const newConversationId = await postQuestion({
        question,
        conversationId: targetConversationId,
        mentions,
      });

      const { newConversationSource, clearNewConversationSource } =
        useChatModeStore.getState();

      if (newConversationId && newConversationId !== conversationId) {
        const conversationStartedSource =
          trackingMetadata?.source ?? newConversationSource ?? null;

        trackAlfredConversationStarted(conversationStartedSource);
      }

      clearNewConversationSource();

      captureQuerySent(newConversationId ?? conversationId);

      const willAutoRedirect =
        newConversationId && isAlfredPage && !config?.alfredEverywhere;

      if (newConversationId && !config?.alfredEverywhere) {
        setReasonerParams({
          conversationId: newConversationId,
        });
      }

      if (newConversationId && !willAutoRedirect) {
        useChat.getState().clearLastCreatedConversationId();
      }

      submitCallback?.();
      onSubmitCallbackHandler();
    };

    if (shouldStartNewConversation) {
      multiSubmitQueueRef.current = multiSubmitQueueRef.current
        .then(submitQuestion)
        .catch(() => undefined);
      await multiSubmitQueueRef.current;
      return;
    }

    await submitQuestion();
  };

  const onSubmit = (
    event: ChatSubmitEvent,
    inputText: string,
    onSubmitCallbackHandler: () => void,
    options?: SubmitOptions
  ) => {
    event.preventDefault();
    submitHandler(inputText, onSubmitCallbackHandler, options);
  };

  const onKeyDown = (
    _: React.KeyboardEvent<HTMLElement>,
    inputText: string,
    onSubmitCallbackHandler: () => void
  ) => {
    submitHandler(inputText, onSubmitCallbackHandler);
  };

  return (
    <ChatInputComposer
      key={remountChatInput ?? 'chat-input'}
      ref={inputRef}
      handlers={{
        onSubmit,
        onKeyDown,
        onSelect: () => {},
        onAbort: () => abortRequest(conversationId),
      }}
      config={{
        ...config,
        isCustomizedDisabled: lastState?.status === 'Fatal',
        isLoading,
      }}
      trackingMetadata={trackingMetadata}
      promptRefinementContext={conversationId ? { conversationId } : undefined}
    />
  );
};
